import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { groupBy, isEmpty, noop, reject } from 'lodash';
import {
  useDiscoveryIdSelector,
  useOpenVizDatasetAttributesSelector,
  useOpenVizSelector,
} from '../../common/redux/selectors/viz-selector.hook';
import { Viz } from '../VizUtil';
import {
  IAnyAttribute,
  ITimeCalcAttribute,
} from '../../datasets/interfaces/attribute.interface';
import { useSelector } from 'react-redux';
import { VIZ_SELECTORS } from '../../common/redux/selectors/viz-selectors';
import { isAdmin as isUserAdmin } from '../../common/redux/selectors/AccountSelectors';
import { IViz } from '../interfaces';
import { IVizLayoutPanelContext } from './viz-layout-panel.interfaces';
import { MINIMUM_FIELDS_COUNT } from '../../common';

type Props = {
  children: ReactNode;
};

export const VizLayoutPanelContext = createContext<IVizLayoutPanelContext>({
  viz: {} as IViz,
  searchFieldsValue: '',
  hideFields: {},
  onFieldSearch: noop,
  fields: [],
  visibleFields: [],
  invisibleFields: [],
  fieldsInPlay: [],
  showHiddenFields: false,
  isSearchFieldsValueValid: false,
  getVirtualizationData: () => ({
    flatFieldList: [],
    totalItemCount: 0,
    shouldUseVirtualization: false,
  }),
});

export const VizLayoutPanelContextProvider = ({ children }: Props) => {
  const [searchFieldsValue, setSearchFieldsValue] = useState('');
  const [hideFields, setHideFields] = useState<{ [name: string]: boolean }>({});
  const isAdmin = useSelector(({ account }: any) => isUserAdmin(account));

  const discoveryId = useDiscoveryIdSelector();
  const viz = useOpenVizSelector({
    discoveryId,
  });
  const datasetAttributes = useOpenVizDatasetAttributesSelector({
    discoveryId,
  });
  const calcFields = useSelector(state =>
    VIZ_SELECTORS.getActiveVizCalcFields(state, { discoveryId }),
  );
  const timeHierarchies = useSelector(state =>
    VIZ_SELECTORS.getActiveVizTimeHierarchies(state, { discoveryId }),
  );

  const fields = useMemo(() => {
    const fieldsWithCalcs = reject(calcFields, 'parentField');
    const hydratedFields = Viz.hydrateTimeHierarchyFields({
      calcFields: fieldsWithCalcs,
      timeHierarchies,
      datasetAttributes,
    });

    return hydratedFields.fields || [];
  }, [datasetAttributes, calcFields, timeHierarchies]);

  const { visibleFields, invisibleFields } = useMemo(() => {
    if (!fields || fields.length === 0) {
      return { visibleFields: [], invisibleFields: [] };
    }

    const unavailableNames = Viz.getUnavailableFieldNames(viz);
    const queryableFields = fields.flatMap((f: IAnyAttribute) => {
      if (unavailableNames.has(f.name)) {
        return [];
      }
      if (isEmpty(f.children)) {
        return [f];
      }
      const children = f.children.filter(c => !unavailableNames.has(c.name));
      if (children.length === 0) {
        return [];
      }
      return [children.length === f.children.length ? f : { ...f, children }];
    });

    const fieldsByVisibility = groupBy(queryableFields, (f: IAnyAttribute) =>
      f?.hidden ? 'hidden' : 'visible',
    );

    return {
      visibleFields: fieldsByVisibility.visible || [],
      invisibleFields: fieldsByVisibility.hidden || [],
    };
  }, [fields, viz]);

  const fieldsInPlay = Viz.getAllFieldsInPlay(viz?.layout || {});

  const onFieldSearch = useCallback(
    (searchValue: string) => {
      const newHideFields: { [name: string]: boolean } = {};
      if (searchValue.length < 2) {
        setSearchFieldsValue(searchValue);
        setHideFields({});
        return;
      }

      const lowerSearchValue = searchValue.toLowerCase();
      const inPlayFieldNames = new Set(fieldsInPlay.map(field => field.name));

      fields.forEach(f => {
        const fieldDisplayName = f.displayName || f.name;
        const isFound = fieldDisplayName
          .toLowerCase()
          .includes(lowerSearchValue);

        if (!isEmpty(f.children)) {
          const childrenToHide: string[] = [];

          for (const childField of f.children) {
            const isChildInPlay = inPlayFieldNames.has(childField.name);
            const childMatches = (
              childField as ITimeCalcAttribute
            ).fieldListDisplayName
              ?.toLowerCase()
              .includes(lowerSearchValue);

            if (!isChildInPlay && !childMatches) {
              childrenToHide.push(childField.name);
            }
          }

          if (childrenToHide.length > 0 && !isFound) {
            childrenToHide.forEach(name => {
              newHideFields[name] = true;
            });
            newHideFields[f.name] = true;
          }
        } else if (!isFound) {
          newHideFields[f.name] = true;
        }
      });

      setSearchFieldsValue(searchValue);
      setHideFields(newHideFields);
    },
    [fields, fieldsInPlay],
  );

  const isSearchFieldsValueValid = searchFieldsValue.length >= 2;
  const showHiddenFields = isAdmin && isSearchFieldsValueValid;

  const getVirtualizationData = useCallback(
    (expandTimeFields: string[]) => {
      const flatFieldList: Array<{
        field: IAnyAttribute;
        type: 'parent' | 'child' | 'standalone' | 'separator';
        parentField?: IAnyAttribute;
        depth: number;
        isVisible: boolean;
      }> = [];

      const processFields = (
        fieldsToProcess: IAnyAttribute[],
        isHiddenSection = false,
      ) => {
        fieldsToProcess.forEach(field => {
          // Skip if field is hidden by search
          if (hideFields[field.name]) {
            return;
          }

          if (!isEmpty(field.children)) {
            // Add parent field
            flatFieldList.push({
              field,
              type: 'parent',
              depth: 0,
              isVisible: !isHiddenSection,
            });

            // Only add children if parent is expanded
            const isExpanded =
              expandTimeFields.includes(field.name) || isSearchFieldsValueValid;
            if (isExpanded) {
              field.children?.forEach(childField => {
                if (!hideFields[childField.name]) {
                  flatFieldList.push({
                    field: childField,
                    type: 'child',
                    parentField: field,
                    depth: 1,
                    isVisible: !isHiddenSection,
                  });
                }
              });
            }
          } else {
            // Standalone field
            flatFieldList.push({
              field,
              type: 'standalone',
              depth: 0,
              isVisible: !isHiddenSection,
            });
          }
        });
      };

      // Process visible fields first
      processFields(visibleFields, false);

      // Add hidden fields if admin and search is active
      if (showHiddenFields) {
        // Add a separator item for hidden fields section
        flatFieldList.push({
          field: {
            name: '__hidden_separator__',
            displayName: 'Hidden Fields',
          } as IAnyAttribute,
          type: 'separator',
          depth: 0,
          isVisible: false,
        });
        processFields(invisibleFields, true);
      }

      return {
        flatFieldList,
        totalItemCount: flatFieldList.length,
        shouldUseVirtualization: flatFieldList.length > MINIMUM_FIELDS_COUNT,
      };
    },
    [
      visibleFields,
      invisibleFields,
      hideFields,
      showHiddenFields,
      isSearchFieldsValueValid,
    ],
  );

  const contextValue = useMemo(
    () => ({
      viz,
      searchFieldsValue,
      hideFields,
      onFieldSearch,
      datasetAttributes,
      calcFields,
      timeHierarchies,
      fields,
      fieldsInPlay,
      visibleFields,
      invisibleFields,
      showHiddenFields,
      isSearchFieldsValueValid,
      getVirtualizationData,
    }),
    [
      viz,
      searchFieldsValue,
      hideFields,
      onFieldSearch,
      datasetAttributes,
      calcFields,
      timeHierarchies,
      fields,
      fieldsInPlay,
      visibleFields,
      invisibleFields,
      showHiddenFields,
      isSearchFieldsValueValid,
      getVirtualizationData,
    ],
  );

  return (
    <VizLayoutPanelContext.Provider value={contextValue}>
      {children}
    </VizLayoutPanelContext.Provider>
  );
};

export const useVizLayoutPanelContext = () => useContext(VizLayoutPanelContext);
